import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import {
  getSuppliers, createSupplier, updateSupplier, deleteSupplier, SUPPLIER_STATUSES,
} from "../lib/suppliers";
import SupplierForm from "./SupplierForm";

const PAGE_SIZE = 25;
const STATUS_COLOR = { active: C.success, inactive: C.textDim };

function StatusBadge({ status }) {
  return <span style={S.pill(STATUS_COLOR[status] || C.textDim)}>{status || "—"}</span>;
}

function Th({ label, col, sortBy, sortDir, onSort, style }) {
  const active = sortBy === col;
  return (
    <th style={{ ...S.th, ...style }} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default function SupplierManager({ autoNew, onAutoNew }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState(null); // supplier | {} | null

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data, count: total } = await getSuppliers({ search, status, page, pageSize: PAGE_SIZE, sortBy, sortDir });
      setRows(data); setCount(total);
    } catch (e) {
      setError(e.message || "Could not load suppliers.");
    } finally {
      setLoading(false);
    }
  }, [search, status, sortBy, sortDir, page]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setPage(1); };

  const save = async (payload) => {
    if (editing && editing.id) await updateSupplier(editing.id, payload);
    else await createSupplier(payload);
    setEditing(null);
    await load();
  };
  const del = async (s) => {
    if (!window.confirm(`Delete "${s.company_name}" (${s.supplier_number})?`)) return;
    try { await deleteSupplier(s.id); await load(); } catch (e) { setError(e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 140 };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search number, company, contact, email, VAT…" value={search}
          onChange={(e) => onSearch(e.target.value)} aria-label="Search suppliers" />
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {SUPPLIER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} supplier{count === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={() => setEditing({})}>+ New Supplier</button>
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={load}>Retry</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="Number" col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Company" col="name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Contact</th>
              <th style={S.th}>Email</th>
              <th style={S.th}>Phone</th>
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th} aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={7} style={S.empty}>
                {hasFilters ? (
                  <div>No suppliers match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No suppliers yet</div>
                    <div style={{ marginBottom: 12 }}>Add your first supplier to start building your procurement network.</div>
                    <button style={S.btn()} onClick={() => setEditing({})}>+ New Supplier</button>
                  </div>
                )}
              </td></tr>
            ) : rows.map((s) => (
              <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => setEditing(s)}>
                <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{s.supplier_number}</span></td>
                <td style={S.td}>
                  <strong>{s.company_name}</strong>
                  {s.trading_name && <div style={{ color: C.textMuted, fontSize: 12 }}>t/a {s.trading_name}</div>}
                </td>
                <td style={S.td}>{s.contact_name || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td}>{s.email || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td}>{s.phone || s.mobile || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td}><StatusBadge status={s.status} /></td>
                <td style={S.td} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${s.company_name}`} onClick={() => setEditing(s)}>✎</button>
                    <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${s.company_name}`} onClick={() => del(s)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <SupplierForm initial={editing} onCancel={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
}
